type Props = {
  values: number[];
  width?: number;
  height?: number;
  color?: string;
  ariaLabel?: string;
};

/**
 * Sparkline SVG inline — affiche une polyline simple sur une série de valeurs.
 * Pas de dépendance externe : pure SVG, dimensions fixes, couleur Tailwind via currentColor.
 * Gère les cas dégénérés (0 ou 1 valeur, toutes valeurs identiques).
 */
export default function Sparkline({
  values,
  width = 80,
  height = 24,
  color = 'text-ktm-orange',
  ariaLabel,
}: Props) {
  // Pas assez de points : on rend un trait plat discret.
  if (!values || values.length < 2) {
    return (
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        className={color}
        role="img"
        aria-label={ariaLabel ?? 'Aucune tendance disponible'}
      >
        <line
          x1={0}
          y1={height / 2}
          x2={width}
          y2={height / 2}
          stroke="currentColor"
          strokeWidth={1.5}
          strokeOpacity={0.3}
          strokeLinecap="round"
        />
      </svg>
    );
  }

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min;
  const stepX = values.length > 1 ? width / (values.length - 1) : 0;
  // Padding vertical 2px pour éviter que la ligne touche les bords.
  const padY = 2;
  const innerH = height - padY * 2;

  const points = values
    .map((v, i) => {
      const x = i * stepX;
      // Si toutes valeurs identiques, centre la ligne.
      const norm = range === 0 ? 0.5 : (v - min) / range;
      const y = padY + innerH - norm * innerH;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(' ');

  const lastX = (values.length - 1) * stepX;
  const lastVal = values[values.length - 1] ?? 0;
  const lastNorm = range === 0 ? 0.5 : (lastVal - min) / range;
  const lastY = padY + innerH - lastNorm * innerH;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={color}
      role="img"
      aria-label={ariaLabel ?? `Tendance sur ${values.length} points`}
    >
      <polyline
        points={points}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle cx={lastX} cy={lastY} r={1.75} fill="currentColor" />
    </svg>
  );
}
